import React, { useRef, useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import { cloneDeep } from 'lodash';
import { Dropdown, Menu, Input } from 'antd';
import Tree from '../../common/library/Tree';
import { treeDFS } from '../../util';
import useThrottleForEvent from '../../hooks/useThrottleForEvent';
import useCreateCallbackForEvent from '../../hooks/useCreateCallbackForEvent';
import './tree.scss';
import { inWindow } from 'api';
const forbidPropsKey = ['onOuterDrop', 'onRightClick', 'treeData', 'expandedKeys', 'onExpand', 'expandedKeys', 'draggable'];
const filterCustomProps = (props) => {
    const res = Object.assign({}, props);
    forbidPropsKey.forEach(key => (res[key] = undefined));
    return res;
};
var TreeEditState;
(function (TreeEditState) {
    TreeEditState["ADD"] = "ADD";
    TreeEditState["EDIT"] = "EDIT";
    TreeEditState["LOADING"] = "LOADING";
    TreeEditState["DEFAULT"] = "DEFAULT";
})(TreeEditState || (TreeEditState = {}));
const defaultNodeModel = {
    key: '',
    _parentKey: '',
    title: () => '',
    _isTemp: true,
    _state: TreeEditState.DEFAULT,
    _name: '',
    selectable: false,
    checkable: false,
};
const defaultBeforeRightClick = () => true;
const cloneTree = (data, parentNode) => {
    if (Array.isArray(data)) {
        return data.map(data => {
            if (data === null || data === void 0 ? void 0 : data.children) {
                return Object.assign(Object.assign({}, data), { children: cloneTree(data.children, data), _parentKey: parentNode === null || parentNode === void 0 ? void 0 : parentNode.key });
            }
            else {
                return Object.assign(Object.assign({}, data), { _parentKey: parentNode === null || parentNode === void 0 ? void 0 : parentNode.key });
            }
        });
    }
    else {
        if (data === null || data === void 0 ? void 0 : data.children) {
            return Object.assign(Object.assign({}, data), { children: cloneTree(data.children, data), _parentKey: parentNode === null || parentNode === void 0 ? void 0 : parentNode.key });
        }
        else {
            return Object.assign(Object.assign({}, data), { _parentKey: parentNode === null || parentNode === void 0 ? void 0 : parentNode.key });
        }
    }
};
const EditTree = forwardRef((props, ref) => {
    const { editAble = false, draggable = false, onOuterDrop, onDrop, outerDropDisabled = false, children, treeData, onRightClick, beforeRightClick = defaultBeforeRightClick, onAddNode, onUpdateNode, onDeleteNode, onExpand, expandedKeys, validateOnAdd, validateOnUpdate, getStrTitleOnUpdate, getStrTitleOnAdd, menu, customBaseTree, dropdownClassName, } = props;
    const [localTreeData, setLocalTreeData] = useState([]);
    const [key2NodeMap, setKey2NodeMap] = useState({});
    const [menuVisible, setMenuVisible] = useState(false);
    const activeNodeKey = useRef('');
    const [localExpandedKeys, setLocalExpandKeys] = useState([]);
    const inputRef = useRef(null);
    const LogicTree = customBaseTree || Tree;
    const hasTempNode = useRef(false);
    const hasTempNodeSelectdTask = useRef(false);
    const igTempId = useRef();
    const tempRightParams = useRef();
    const logicOnExpand = onExpand || ((keys) => setLocalExpandKeys(keys));
    const logicExpandedKeys = expandedKeys || localExpandedKeys;
    const logicDraggable = useMemo(() => {
        if (typeof draggable === 'function') {
            return (node) => {
                if (node === null || node === void 0 ? void 0 : node._isTemp) {
                    return false;
                }
                return draggable(node);
            };
        }
        else {
            return draggable
                ? (node) => {
                    return !(node === null || node === void 0 ? void 0 : node._isTemp);
                }
                : false;
        }
    }, [draggable]);
    const handleTreeDrop = (e) => {
        var _a;
        if ((e === null || e === void 0 ? void 0 : e.dragNode) == null && (e === null || e === void 0 ? void 0 : e.dragNodesKeys) == null) {
            try {
                const { node } = e;
                const { nodeData } = node;
                if (!outerDropDisabled && onOuterDrop && !((_a = nodeData === null || nodeData === void 0 ? void 0 : nodeData.entry) === null || _a === void 0 ? void 0 : _a._isTempNode)) {
                    onOuterDrop(e);
                }
            }
            catch (e) {
                console.error('[Error EditTree handleTreeDrop]', e);
            }
        }
        else {
            onDrop && onDrop(e);
        }
    };
    const handleTreeRightClick = (params) => {
        const { node } = params;
        if (node) {
            if (node._isTemp === true) {
                return false;
            }
            activeNodeKey.current = node.key + '';
            tempRightParams.current = params;
            if (beforeRightClick && beforeRightClick(params)) {
                onRightClick && onRightClick(params);
            }
        }
    };
    const addNode = (nodeKey) => {
        if (hasTempNode.current && hasEditInputEl()) {
            return;
        }
        const key = nodeKey || activeNodeKey.current;
        const node = key2NodeMap[key];
        if (node) {
            if (key) {
                logicOnExpand([...new Set([...logicExpandedKeys, key])], {
                    expanded: true,
                    node,
                });
            }
            setTimeout(() => {
                appendInputNode(node);
                setLocalTreeData([...localTreeData]);
            }, 10);
        }
        else {
            setLocalTreeData([...localTreeData, getAddTempNode()]);
        }
        hasTempNodeSelectdTask.current = true;
        hasTempNode.current = true;
    };
    const addNodeForEvent = useCreateCallbackForEvent(addNode);
    const appendNode = (nodeKey) => {
        if (hasTempNode.current && hasEditInputEl()) {
            return;
        }
        const key = nodeKey || activeNodeKey;
        const node = key2NodeMap[key];
        if (node) {
            const res = insertInputNode(node);
            setLocalTreeData([...res]);
        }
        else {
            setLocalTreeData([...localTreeData, getAddTempNode()]);
        }
        (hasTempNodeSelectdTask.current = true), (hasTempNode.current = true);
    };
    const appendNodeForEvent = useCreateCallbackForEvent(appendNode);
    const hasEditInputEl = () => {
        if (inWindow()) {
            const isSafari = navigator.userAgent.toLowerCase().includes('safari/');
            if (!isSafari) {
                return true;
            }
            const inputEl = document.querySelector('.folder-tree-wrap .ant-tree-treenode .edit-tree-input .ant-input');
            if (inputEl) {
                return true;
            }
            else {
                return false;
            }
        }
        return true;
    };
    const updateNode = (nodeKey) => {
        if (hasTempNode.current && hasEditInputEl()) {
            return;
        }
        const key = nodeKey || activeNodeKey.current;
        const node = key2NodeMap[key];
        if (node) {
            const defaultNodeName = getStrTitleOnUpdate ? getStrTitleOnUpdate(localTreeData, node) : typeof (node === null || node === void 0 ? void 0 : node.title) === 'string' ? node.title : '';
            node._state = TreeEditState.EDIT;
            node._name = defaultNodeName;
            node._isTemp = true;
            editNode(node);
            setLocalTreeData([...localTreeData]);
            hasTempNode.current = true;
        }
    };
    const updateNodeForEvent = useCreateCallbackForEvent(updateNode);
    const deleteNode = (nodeKey) => {
        const key = nodeKey || activeNodeKey.current;
        const node = key2NodeMap[key];
        if (node) {
            handleNodeDelete(node);
        }
    };
    const deleteNodeForEvent = useCreateCallbackForEvent(deleteNode);
    const handleCancelAddNode = (addNode) => {
        var _a;
        if (addNode && (addNode === null || addNode === void 0 ? void 0 : addNode.key)) {
            const parentNode = (addNode === null || addNode === void 0 ? void 0 : addNode._parentKey) ? key2NodeMap[addNode === null || addNode === void 0 ? void 0 : addNode._parentKey] : null;
            if (parentNode) {
                parentNode.children = (_a = parentNode.children) === null || _a === void 0 ? void 0 : _a.filter(node => (node === null || node === void 0 ? void 0 : node.key) !== addNode.key);
                setLocalTreeData([...localTreeData]);
            }
            else {
                setLocalTreeData(localTreeData.filter(node => (node === null || node === void 0 ? void 0 : node.key) !== addNode.key));
            }
            hasTempNode.current = false;
        }
    };
    const cancelNodeAdded = useCreateCallbackForEvent(handleCancelAddNode);
    const handleCancelEditNode = (editNode) => {
        if (editNode) {
            editNode._isTemp = false;
            editNode._state = TreeEditState.DEFAULT;
            editNode.title = editNode._name;
            setLocalTreeData([...localTreeData]);
        }
    };
    const getAddTempNode = (parentNode) => {
        const node = cloneDeep(defaultNodeModel);
        const defaultNodeName = getStrTitleOnAdd ? getStrTitleOnAdd(localTreeData, parentNode) : typeof (node === null || node === void 0 ? void 0 : node.title) === 'string' ? node.title : '';
        node.key = new Date().getTime() + '';
        node._state = TreeEditState.ADD;
        node._name = defaultNodeName;
        node._parentKey = (parentNode === null || parentNode === void 0 ? void 0 : parentNode.key) ? (parentNode === null || parentNode === void 0 ? void 0 : parentNode.key) + '' : '0';
        node.title = () => {
            return (React.createElement("span", { className: "edit-tree-title-wrap", onClick: e => {
                    e.stopPropagation();
                }, style: { display: 'flex' } },
                node._state == TreeEditState.LOADING && (React.createElement("span", { className: "edit-tree-loading-wrap" },
                    React.createElement(LoadingOutlined, null))),
                React.createElement(Input, { "data-test-id": "folder-tree-add-folder", className: "edit-tree-input", defaultValue: node._name, ref: inputRef, onPressEnter: e => {
                        setTimeout(() => {
                            debounceHandleNodeAdd(e, node);
                        }, 700);
                    }, onBlur: e => {
                        setTimeout(() => {
                            if (igTempId.current != node.key) {
                                debounceHandleNodeAdd(e, node);
                            }
                            else {
                                igTempId.current = null;
                            }
                        }, 700);
                    }, disabled: node._state == TreeEditState.LOADING, maxLength: 40, onChange: e => {
                        var _a;
                        node._name = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value.trim();
                    }, suffix: node._state != TreeEditState.LOADING && (React.createElement("span", { className: "folder-input-cancel", onClick: () => {
                            handleCancelAddNode(node);
                            igTempId.current = node.key;
                        } }, "\u53D6\u6D88")) })));
        };
        return node;
    };
    const getAppendTempNode = (parentNode) => {
        const node = cloneDeep(defaultNodeModel);
        const defaultNodeName = getStrTitleOnAdd ? getStrTitleOnAdd(localTreeData, parentNode) : typeof (node === null || node === void 0 ? void 0 : node.title) === 'string' ? node.title : '';
        node.key = new Date().getTime() + '';
        node._state = TreeEditState.ADD;
        node._name = defaultNodeName;
        node._parentKey = (parentNode === null || parentNode === void 0 ? void 0 : parentNode.key) ? (parentNode === null || parentNode === void 0 ? void 0 : parentNode.key) + '' : '0';
        node.title = () => {
            return (React.createElement("span", { className: "edit-tree-title-wrap", onClick: e => {
                    e.stopPropagation();
                }, style: { display: 'flex' } },
                node._state == TreeEditState.LOADING && (React.createElement("span", { className: "edit-tree-loading-wrap" },
                    React.createElement(LoadingOutlined, null))),
                React.createElement(Input, { "data-test-id": "folder-tree-add-folder", className: "edit-tree-input", defaultValue: node._name, ref: inputRef, onPressEnter: e => {
                        setTimeout(() => {
                            if (igTempId.current != node.key) {
                                debounceHandleNodeAdd(e, node);
                            }
                        }, 700);
                    }, onBlur: e => {
                        setTimeout(() => {
                            if (igTempId.current != node.key) {
                                debounceHandleNodeAdd(e, node);
                            }
                            else {
                                igTempId.current = null;
                            }
                        }, 700);
                    }, disabled: node._state == TreeEditState.LOADING, maxLength: 40, onChange: e => {
                        var _a;
                        node._name = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value.trim();
                    }, suffix: node._state != TreeEditState.LOADING && (React.createElement("span", { className: "folder-input-cancel", onClick: () => {
                            cancelNodeAdded(node);
                            igTempId.current = node.key;
                        } }, "\u53D6\u6D88")) })));
        };
        return node;
    };
    const appendInputNode = (parentNode) => {
        var _a;
        if (parentNode) {
            const tempNode = getAddTempNode(parentNode);
            if (parentNode === null || parentNode === void 0 ? void 0 : parentNode.children) {
                (_a = parentNode.children) === null || _a === void 0 ? void 0 : _a.unshift(tempNode);
            }
            else {
                parentNode.children = [tempNode];
            }
        }
    };
    const insertInputNode = (brotherNode) => {
        let res = [...localTreeData];
        if (brotherNode) {
            const commonParentNode = (brotherNode === null || brotherNode === void 0 ? void 0 : brotherNode._parentKey) && key2NodeMap[brotherNode === null || brotherNode === void 0 ? void 0 : brotherNode._parentKey];
            const tempNode = getAppendTempNode(commonParentNode);
            if (commonParentNode) {
                const children = commonParentNode.children || [];
                const index = children.findIndex(item => item.key === (brotherNode === null || brotherNode === void 0 ? void 0 : brotherNode.key));
                if (index > -1) {
                    res = [...children];
                    res.splice(index + 1, 0, tempNode);
                    commonParentNode.children = res;
                }
            }
            else {
                const index = res === null || res === void 0 ? void 0 : res.findIndex(item => item.key == (brotherNode === null || brotherNode === void 0 ? void 0 : brotherNode.key));
                if (index > -1) {
                    res === null || res === void 0 ? void 0 : res.splice(index + 1, 0, tempNode);
                    return [...res];
                }
            }
        }
        return [...localTreeData];
    };
    const editNode = (node) => {
        if (node) {
            node.title = () => {
                return (React.createElement("span", { className: "edit-tree-title-wrap", onClick: e => {
                        e.stopPropagation();
                    }, style: { display: 'flex' } },
                    node._state == TreeEditState.LOADING && (React.createElement("span", { className: "edit-tree-loading-wrap" },
                        React.createElement(LoadingOutlined, null))),
                    React.createElement(Input, { "data-test-id": "folder-tree-rename-folder", className: "edit-tree-input", defaultValue: node._name, ref: inputRef, onPressEnter: e => {
                            setTimeout(() => {
                                debounceHandleNodeUpdate(e, node);
                            }, 700);
                        }, onBlur: e => {
                            setTimeout(() => {
                                debounceHandleNodeUpdate(e, node);
                            }, 700);
                        }, disabled: node._state == TreeEditState.LOADING, maxLength: 40, onChange: e => {
                            var _a;
                            node._name = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value.trim();
                        }, suffix: node._state != TreeEditState.LOADING && (React.createElement("span", { className: "folder-input-cancel", onClick: () => {
                                handleCancelEditNode(node);
                            } }, "\u53D6\u6D88")) })));
            };
        }
    };
    const handleNodeAdd = (e, node) => {
        var _a;
        if (!(node === null || node === void 0 ? void 0 : node._isTemp)) {
            return false;
        }
        if (validateOnAdd && !validateOnAdd(localTreeData, node)) {
            return false;
        }
        if (onAddNode) {
            const parentNode = (node === null || node === void 0 ? void 0 : node._parentKey) && key2NodeMap[node === null || node === void 0 ? void 0 : node._parentKey];
            let keyList = (localTreeData === null || localTreeData === void 0 ? void 0 : localTreeData.map(item => item.key)) || [];
            if (parentNode && (parentNode === null || parentNode === void 0 ? void 0 : parentNode.children)) {
                keyList = (_a = parentNode === null || parentNode === void 0 ? void 0 : parentNode.children) === null || _a === void 0 ? void 0 : _a.map(item => item.key + '');
            }
            const res = onAddNode(e, node, keyList);
            if ((res === null || res === void 0 ? void 0 : res.then) != null) {
                node._state = TreeEditState.LOADING;
                setLocalTreeData([...localTreeData]);
                res
                    .then(() => {
                })
                    .catch(() => {
                    node._state = TreeEditState.ADD;
                    setLocalTreeData([...localTreeData]);
                });
            }
            else {
                if (res !== false) {
                    node._state = TreeEditState.LOADING;
                    setLocalTreeData([...localTreeData]);
                }
            }
        }
    };
    const debounceHandleNodeAdd = useThrottleForEvent(handleNodeAdd, 500);
    const handleNodeUpdate = (e, node) => {
        if (!(node === null || node === void 0 ? void 0 : node._isTemp)) {
            return false;
        }
        if (validateOnUpdate && !validateOnUpdate(localTreeData, node)) {
            return false;
        }
        if (onUpdateNode) {
            const res = onUpdateNode(e, node);
            if ((res === null || res === void 0 ? void 0 : res.then) != null) {
                node._state = TreeEditState.LOADING;
                setLocalTreeData([...localTreeData]);
                res
                    .then(() => {
                })
                    .catch(() => {
                    node._state = TreeEditState.EDIT;
                    setLocalTreeData([...localTreeData]);
                });
            }
            else {
                if (res !== false) {
                    node._state = TreeEditState.LOADING;
                    setLocalTreeData([...localTreeData]);
                }
            }
        }
    };
    const debounceHandleNodeUpdate = useThrottleForEvent(handleNodeUpdate, 500);
    const handleNodeDelete = (node) => {
        if (node && onDeleteNode) {
            const res = onDeleteNode(node);
            if ((res === null || res === void 0 ? void 0 : res.then) != null) {
                node._state = TreeEditState.LOADING;
                setLocalTreeData([...localTreeData]);
                res
                    .then(() => {
                })
                    .catch(() => {
                    node._state = TreeEditState.DEFAULT;
                    setLocalTreeData([...localTreeData]);
                });
            }
            else {
                if (!!res) {
                    node._state = TreeEditState.LOADING;
                    setLocalTreeData([...localTreeData]);
                }
            }
        }
    };
    const debounceHandleNodeDelete = useThrottleForEvent(handleNodeDelete, 500);
    const focusInput = useCallback(() => {
        setTimeout(() => {
            var _a;
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus({
                cursor: 'all',
            });
        }, 200);
        setTimeout(() => {
            var _a;
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus({
                cursor: 'all',
            });
        }, 1000);
        setTimeout(() => {
            var _a;
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus({
                cursor: 'all',
            });
        }, 1500);
    }, []);
    const defaultMenuElement = useMemo(() => {
        return (React.createElement(Menu, { mode: "horizontal" },
            React.createElement(Menu.Item, { key: "add", onClick: () => {
                    addNode();
                    setMenuVisible(false);
                    focusInput();
                } }, "\u65B0\u5EFA"),
            React.createElement(Menu.Item, { key: "edit", onClick: () => {
                    updateNode();
                    setMenuVisible(false);
                    focusInput();
                } }, "\u7F16\u8F91"),
            React.createElement(Menu.Item, { key: "delete", onClick: () => {
                    deleteNode();
                    setMenuVisible(false);
                } }, "\u5220\u9664")));
    }, [addNode, updateNode, deleteNode]);
    useEffect(() => {
        const localData = cloneTree(treeData);
        setLocalTreeData(localData);
        const map = {};
        treeDFS(localData, (node) => {
            if (node === null || node === void 0 ? void 0 : node.key) {
                map[(node === null || node === void 0 ? void 0 : node.key) + ''] = node;
            }
        });
        setKey2NodeMap(map);
        hasTempNode.current = false;
    }, [treeData]);
    useImperativeHandle(ref, () => ({
        addNode: addNodeForEvent,
        appendNode: appendNodeForEvent,
        updateNode: updateNodeForEvent,
        deleteNode: deleteNodeForEvent,
    }));
    useEffect(() => {
        if (hasTempNodeSelectdTask.current) {
            setTimeout(() => {
                var _a;
                ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus) && inputRef.current.focus({ cursor: 'all' });
            }, 300);
            hasTempNodeSelectdTask.current = false;
        }
    }, [localTreeData]);
    const TreeElement = (React.createElement(LogicTree, Object.assign({ blockNode: true }, filterCustomProps(props), { expandedKeys: [...logicExpandedKeys], onExpand: logicOnExpand, treeData: localTreeData, onDrop: handleTreeDrop, onRightClick: handleTreeRightClick, draggable: logicDraggable }), children));
    const logicMenu = useMemo(() => {
        let res = null;
        if (menu) {
            if (typeof menu == 'function') {
                res = menu(key2NodeMap[activeNodeKey.current]);
            }
            else {
                res = menu;
            }
        }
        else {
            res = defaultMenuElement;
        }
        return React.createElement("div", { onClick: () => setMenuVisible(false) }, res);
    }, [menu, key2NodeMap, activeNodeKey.current]);
    return (React.createElement("div", { className: "edti-tree-wrap" }, editAble ? (React.createElement(Dropdown, { overlayClassName: `u-tree-dropmenu  ${dropdownClassName || ''}`, overlayStyle: props.overlayStyle || {}, trigger: ['contextMenu'], overlay: logicMenu, visible: menuVisible, onVisibleChange: visible => {
            if (!visible) {
                activeNodeKey.current = '';
            }
            if (visible) {
                if (beforeRightClick(tempRightParams.current)) {
                    setMenuVisible(visible);
                }
            }
            else {
                setMenuVisible(visible);
            }
        } },
        React.createElement("div", { style: { width: '100%', height: '100%' } }, TreeElement))) : (TreeElement)));
});
export default EditTree;
